import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Users, CreditCard, TrendingUp, DollarSign, Target, Zap, RefreshCw, Download, FileText, FileSpreadsheet, Calendar, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

export const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [dateRange, setDateRange] = useState('30');
  const [tierFilter, setTierFilter] = useState('all');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, tierFilter]);

  const fetchAnalytics = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Build query params
      const params = new URLSearchParams({
        days: dateRange
      });
      if (tierFilter !== 'all') {
        params.append('tier_filter', tierFilter);
      }

      // Fetch overview
      const overviewRes = await fetch(`${backendUrl}/api/analytics/overview?${params}`, {
        credentials: 'include'
      });

      if (!overviewRes.ok) throw new Error('Failed to fetch analytics');

      const overviewData = await overviewRes.json();
      setOverview(overviewData);

      // Fetch chart data
      const chartRes = await fetch(`${backendUrl}/api/analytics/revenue-chart?${params}`, {
        credentials: 'include'
      });

      if (chartRes.ok) {
        const chart = await chartRes.json();
        setChartData(chart);
      }

      if (isRefresh) toast.success('Analytics refreshed');
    } catch (error) {
      console.error('Analytics error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const params = new URLSearchParams({ days: dateRange });
      if (tierFilter !== 'all') {
        params.append('tier_filter', tierFilter);
      }

      const response = await fetch(`${backendUrl}/api/analytics/export/${format}?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehiclebuddy_analytics.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pb-6 max-w-7xl">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Users',
      value: overview?.total_users || 0,
      icon: Users,
      color: 'hsl(var(--brand-sky))',
      bgColor: 'hsl(var(--brand-sky))/10'
    },
    {
      title: 'Active Subscriptions',
      value: overview?.active_subscriptions || 0,
      icon: CreditCard,
      color: 'hsl(var(--brand-teal))',
      bgColor: 'hsl(var(--brand-teal))/10',
      subtitle: `${overview?.free_users || 0} free users`
    },
    {
      title: 'Monthly Revenue (MRR)',
      value: `£${overview?.mrr || 0}`,
      icon: DollarSign,
      color: 'hsl(var(--brand-green))',
      bgColor: 'hsl(var(--brand-green))/10',
      subtitle: `${overview?.growth_rate > 0 ? '+' : ''}${overview?.growth_rate || 0}% growth`
    },
    {
      title: 'Conversion Rate',
      value: `${overview?.conversion_rate || 0}%`,
      icon: Target,
      color: 'hsl(var(--brand-coral))',
      bgColor: 'hsl(var(--brand-coral))/10',
      subtitle: `${overview?.recent_subscriptions || 0} new this month`
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6 max-w-7xl"
    >
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">
              <BarChart3 className="h-8 w-8" />
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">Track growth and revenue metrics</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleExport('csv')}
              variant="outline"
              size="sm"
              className="gap-2"
              data-testid="export-csv-button"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export CSV
            </Button>
            <Button
              onClick={() => handleExport('pdf')}
              variant="outline"
              size="sm"
              className="gap-2"
              data-testid="export-pdf-button"
            >
              <FileText className="h-4 w-4" />
              Export PDF
            </Button>
            <Button
              onClick={() => fetchAnalytics(true)}
              disabled={refreshing}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 border">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>
          
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]" data-testid="date-range-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="180">Last 6 Months</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>

          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-[180px]" data-testid="tier-filter">
              <TrendingUp className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="premium">Premium Only</SelectItem>
              <SelectItem value="family">Family Only</SelectItem>
              <SelectItem value="fleet">Fleet Only</SelectItem>
            </SelectContent>
          </Select>

          {(dateRange !== '30' || tierFilter !== 'all') && (
            <Button
              onClick={() => {
                setDateRange('30');
                setTierFilter('all');
              }}
              variant="ghost"
              size="sm"
              className="text-xs"
            >
              Reset Filters
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: stat.bgColor }}
                  >
                    <Icon className="h-5 w-5" style={{ color: stat.color }} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                  {stat.subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Tier Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Subscription Tiers</CardTitle>
            <CardDescription>Distribution across plans</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { tier: 'free', label: 'Free', color: 'hsl(var(--muted-foreground))' },
              { tier: 'premium', label: 'Premium', color: 'hsl(var(--brand-sky))' },
              { tier: 'family', label: 'Family', color: 'hsl(var(--brand-teal))' },
              { tier: 'fleet', label: 'Fleet', color: 'hsl(var(--brand-coral))' }
            ].map(({ tier, label, color }) => {
              const count = overview?.tiers_breakdown?.[tier] || 0;
              const percentage = overview?.total_users 
                ? ((count / overview.total_users) * 100).toFixed(1) 
                : 0;

              return (
                <div key={tier}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ background: color }}
                      />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {count} ({percentage}%)
                    </div>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full transition-all duration-300"
                      style={{ 
                        width: `${percentage}%`,
                        background: color
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Additional Metrics</CardTitle>
            <CardDescription>Usage statistics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">Total AI Scans</p>
                  <p className="text-xs text-muted-foreground">All time</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{overview?.total_scans || 0}</p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">Total Vehicles</p>
                  <p className="text-xs text-muted-foreground">Registered</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{overview?.total_vehicles || 0}</p>
            </div>

            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground">
                Last updated: {overview?.metrics_updated_at 
                  ? new Date(overview.metrics_updated_at).toLocaleString('en-GB')
                  : 'N/A'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Revenue Chart */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Subscription Growth (Last 30 Days)</CardTitle>
              <CardDescription>Daily new subscriptions by tier</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorPremium" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--brand-sky))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--brand-sky))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorFamily" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--brand-teal))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--brand-teal))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorFleet" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--brand-coral))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--brand-coral))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    style={{ fontSize: 12 }}
                  />
                  <YAxis style={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="premium" 
                    stackId="1"
                    stroke="hsl(var(--brand-sky))" 
                    fill="url(#colorPremium)" 
                    name="Premium"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="family" 
                    stackId="1"
                    stroke="hsl(var(--brand-teal))" 
                    fill="url(#colorFamily)" 
                    name="Family"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="fleet" 
                    stackId="1"
                    stroke="hsl(var(--brand-coral))" 
                    fill="url(#colorFleet)" 
                    name="Fleet"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Analytics;
